import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import CryptoJS from "crypto-js";
import { client } from "../..";
import { SERVER_URL } from '../../databucket';
import {
  setAllFolderData,
  updatePagination,
  appendToFolder,
  moveToDeletedFolder,
  removeEmailFromFolder,
  markAsReadInState,
  setEmailReadStatus,
  setCurrentMailId,
  setFolderMeta,
  initFolderInState,
  mergeInboxPoll,
} from '../notification'
import { setLoader } from '../loaderSlice';
import { VALIDATE_OUTLOOK } from '../../Queries/Emails';
import socket from '../../views/pages/Sales1/socket';


// Strip our own tracking pixels from email bodies before they enter Redux state.
// This prevents the browser from firing open events when we render emails in the dashboard.
function stripTrackingPixels(html) {
  if (!html) return html;
  return html.replace(/<img[^>]*\/api\/track\/open\/[^>]*\/?>/gi, '');
}

// Apply strip to a Graph message object
function sanitizeEmailBody(msg) {
  if (!msg?.body?.content) return msg;
  return { ...msg, body: { ...msg.body, content: stripTrackingPixels(msg.body.content) } };
}

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = error => reject(error);
  });
};

// Dedup fingerprint: subject + sentDateTime rounded to the nearest minute
function emailFingerprint(m) {
  const dt = m.sentDateTime || m.receivedDateTime;
  const rounded = dt ? new Date(Math.floor(new Date(dt).getTime() / 60000) * 60000).toISOString() : '';
  return `${(m.subject || '').toLowerCase().trim()}|${rounded}`;
}

// Merge Graph sent items + SES sent items and dispatch to Redux.
// graphEmails: emails from MS Graph (may be empty).
// sesEmails: emails from SES (may be empty).
// If both are empty, does NOT dispatch — preserves whatever is already in state.
function mergeSentItemsAndDispatch(email, graphEmails, sesEmails, dispatch) {
  if (!graphEmails.length && !sesEmails.length) return;
  const graphIds = new Set(graphEmails.map(m => m.messageId || m.id).filter(Boolean));
  const graphFp  = new Set(graphEmails.map(emailFingerprint));
  const uniqueSes = sesEmails.filter(m =>
    !graphIds.has(m.messageId) && !graphIds.has(m.id) && !graphFp.has(emailFingerprint(m))
  );
  const merged = [...uniqueSes, ...graphEmails].sort(
    (a, b) => new Date(b.sentDateTime || b.receivedDateTime) - new Date(a.sentDateTime || a.receivedDateTime)
  );
  if (merged.length > 0) {
    dispatch(initFolderInState({ user: email, folder: 'sentitems', data: merged }));
  }
}

export const fetchInitialEmails = createAsyncThunk(
  'emails/fetchInitial',
  async (email, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setLoader(true));

      // Fetch Graph folders + SES sent + SES deleted all in parallel so sentitems
      // is only dispatched once with the fully merged data (no intermediate 3-email flash).
      const [res, sesRes, sesDeletedRes] = await Promise.all([
        axios.post(SERVER_URL + '/api/outlook', { email }),
        axios.get(`${SERVER_URL}/api/ses/sent?fromEmail=${encodeURIComponent(email)}&limit=50`).catch(() => ({ data: { emails: [] } })),
        axios.get(`${SERVER_URL}/api/ses/deleted?fromEmail=${encodeURIComponent(email)}&limit=50`).catch(() => ({ data: { emails: [] } })),
      ]);

      const folders = ['Inbox', 'SentItems', 'Drafts', 'DeletedItems', 'JunkEmail'];

      // Detect NEW unread inbox emails vs previously known state.
      // Only emit if we had prior state — on first load prevInbox is empty,
      // so all unread would be treated as "new" which is wrong.
      const prevInbox = getState()?.EmailData?.byUser?.[email]?.folders?.inbox?.data || [];
      const prevIds = new Set(prevInbox.map(m => m.id));
      const newInbox = res.data['Inbox'] || [];
      if (prevInbox.length > 0) {
        const newUnread = newInbox.filter(m => !prevIds.has(m.id) && !m.isRead);
        if (newUnread.length > 0) {
          socket.emit('email:newInbox', { count: newUnread.length, email });
          socket.emit('email:newInboxLocal', { count: newUnread.length });
        }
      }

      const formattedData = {};
      folders.forEach((folder) => {
        const key = folder.toLowerCase();
        formattedData[key] = {
          skip: 0,
          hasMore: true,
          data: (res.data[folder] || []).map(sanitizeEmailBody),
        };
      });
      dispatch(setAllFolderData({ user: email, folders: formattedData }));

      // Merge SES sent emails into sentitems — both Graph and SES data already available,
      // so this dispatches immediately with no visible gap.
      const sesEmails = sesRes.data?.emails || [];
      mergeSentItemsAndDispatch(email, formattedData.sentitems?.data || [], sesEmails, dispatch);

      // Merge SES soft-deleted emails into deleteditems
      const sesDeleted = sesDeletedRes.data?.emails || [];
      if (sesDeleted.length > 0) {
        const graphDeleted = formattedData.deleteditems?.data || [];
        const graphIds = new Set(graphDeleted.map(m => m.id).filter(Boolean));
        const uniqueSesDeleted = sesDeleted.filter(m => !graphIds.has(m.id));
        if (uniqueSesDeleted.length > 0) {
          dispatch(initFolderInState({
            user: email,
            folder: 'deleteditems',
            data: [...uniqueSesDeleted, ...graphDeleted],
          }));
        }
      }
    } catch (err) {
      return rejectWithValue('Failed to load email folders.');
    } finally {
      dispatch(setLoader(false));
    }
  }
);


export const loadMoreEmails = createAsyncThunk(
  'emails/loadMore',
  async ({ user, folder, skip }, { dispatch, rejectWithValue, getState }) => {
    try {
      dispatch(setLoader(true));
      // Resolve Graph folder ID from folderMeta (needed for custom folders)
      const folderMeta = getState()?.EmailData?.byUser?.[user]?.folderMeta || [];
      const folderEntry = folderMeta.find(f => f.key === folder);
      const graphFolder = folderEntry?.id || folder;
      const res = await axios.post(SERVER_URL + '/api/outlook/fetchMore', {
        email: user,
        folder: graphFolder,
        skip: skip + 50,
      });

      const newMessages = (res.data || []).map(sanitizeEmailBody);
      dispatch(appendToFolder({ user, folder, newMessages }));
      dispatch(updatePagination({
        user,
        folder,
        skip: skip + 50,
        hasMore: newMessages.length === 50,
      }));
    } catch (err) {
      return rejectWithValue('Failed to load more emails');
    } finally {
      dispatch(setLoader(false));
    }
  }
);

// Folders where delete = permanent (no recovery needed)
const PERMANENT_DELETE_FOLDERS = ['drafts', 'deleteditems', 'junkemail'];

export const deleteEmailThunk = createAsyncThunk(
  'emails/delete',
  async ({ user, email, activeFolder }, { dispatch, rejectWithValue }) => {
    dispatch(setLoader(true));
    const permanent = PERMANENT_DELETE_FOLDERS.includes(activeFolder);

    try {
      // SES emails live outside Outlook/Graph — delete from DB then move in local Redux.
      // Pass conversationId so the backend deletes all sibling replies in the same conversation,
      // preventing deduplicated reply emails from reappearing after page reload.
      if (email.provider === 'ses') {
        await axios.delete(`${SERVER_URL}/api/ses/sent/${email.id}`, {
          data: { conversationId: email.conversationId, fromEmail: user },
        });
        dispatch(moveToDeletedFolder({
          user,
          folder: activeFolder,
          conversationId: email.conversationId,
          email,
        }));
        return { success: true };
      }

      const res = await axios.delete(SERVER_URL + '/delete-email', {
        data: {
          userId: user,
          conversationId: email.conversationId,
          permanent,
        },
      });
      if (res?.data?.success) {
        if (permanent) {
          // Remove from folder locally — no move to Deleted Items
          dispatch(removeEmailFromFolder({
            user,
            folder: activeFolder,
            conversationId: email.conversationId,
          }));
        } else {
          // Move to Deleted Items locally
          dispatch(moveToDeletedFolder({
            user,
            folder: activeFolder,
            conversationId: email.conversationId,
            email,
          }));
        }
      }

      return res.data;

    } catch (err) {
      console.error('[deleteEmailThunk]', err?.response?.data || err.message);
      return rejectWithValue(err?.response?.data?.error || 'Failed to delete email');
    } finally {
      dispatch(setLoader(false));
    }
  }
);

export const FetchReplysThunk = createAsyncThunk(
  'emails/reply',
  async ({ email, messageId, replyMessageIds }, { dispatch, rejectWithValue }) => {

    try {
      dispatch(setLoader(true));
      const response = await axios.post(SERVER_URL + '/track-replies', {
        userEmail: email,
        internetMessageId: messageId,
        replyMessageIds: replyMessageIds || [],
      });
      dispatch(setCurrentMailId({ messageId, email }));
      return response.data;
    } catch (err) {
      return rejectWithValue('Failed to delete email');
    } finally {
      dispatch(setLoader(false));
    }
  }
);

export const markEmailAsRead = createAsyncThunk(
  'emails/markEmailAsRead',
  async ({ user, messageId, folder, conversationId }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader(true));
      const response = await axios.patch(SERVER_URL + '/mark-as-read', {
        userId: user,
        messageId,
      });
      if (response?.data?.success) {
        dispatch(markAsReadInState({
          folder, conversationId, user
        }))
      }
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to mark as read');
    } finally {
      dispatch(setLoader(false));
    }
  }
);

export const markEmailAsUnread = createAsyncThunk(
  'emails/markEmailAsUnread',
  async ({ user, messageId, folder, conversationId }, { dispatch }) => {
    try {
      await axios.patch(SERVER_URL + '/mark-as-read', { userId: user, messageId, isRead: false });
    } catch { /* non-fatal — state updated regardless */ }
    dispatch(setEmailReadStatus({ folder, conversationId, user, isRead: false }));
  }
);

export const sendEmailsThunk = createAsyncThunk(
  'emails/sendEmails',
  async ({ formData, fileList, editorHTML }, { rejectWithValue }) => {
    try {
      const attachments = await Promise.all(
        fileList.map(async (file) => ({
          filename: file.name,
          contentType: file.type,
          content: await fileToBase64(file.originFileObj),
        }))
      );

      const payload = {
        ...formData,
        body: editorHTML,
        attachments,
      };

      const response = await axios.post(`${SERVER_URL}/send-emails`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 200 && response.data) {
        return response.data; // contains: { totalSent, totalFailed, sendStatus, messageId }
      } else {
        return rejectWithValue("Unexpected response from server.");
      }
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || "Email send failed.");
    }
  }
);

// ── AWS SES send thunk ────────────────────────────────────────────────────────
export const sendSesEmailThunk = createAsyncThunk(
  'emails/sendSesEmail',
  async ({ formData, fileList, editorHTML }, { rejectWithValue }) => {
    try {
      const attachments = await Promise.all(
        (fileList || []).map(async (file) => {
          // S3-backed draft attachment — fetch content from S3 proxy
          if (file.s3Key && !file.originFileObj) {
            try {
              const res = await axios.get(`${SERVER_URL}/api/proxy?url=${encodeURIComponent(file.s3Key)}`, { responseType: 'arraybuffer' });
              const base64 = btoa(new Uint8Array(res.data).reduce((d, b) => d + String.fromCharCode(b), ''));
              return { filename: file.name, contentType: file.type || file.contentType, base64 };
            } catch { return null; }
          }
          return {
            filename: file.name,
            contentType: file.type,
            base64: await fileToBase64(file.originFileObj),
          };
        })
      ).then(results => results.filter(Boolean));

      const payload = { ...formData, body: editorHTML, attachments };
      const response = await axios.post(`${SERVER_URL}/api/ses/send`, payload, {
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.status === 200 && response.data) {
        return response.data;
      }
      return rejectWithValue('Unexpected response from SES.');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.error || 'SES email send failed.');
    }
  }
);

export const fetchMailFolders = createAsyncThunk(
  'emails/fetchMailFolders',
  async (email, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.get(`${SERVER_URL}/api/outlook/folders?email=${encodeURIComponent(email)}`);
      dispatch(setFolderMeta({ user: email, folderMeta: res.data }));
      return res.data;
    } catch (err) {
      return rejectWithValue('Failed to load folder list');
    }
  }
);

export const fetchFolderOnDemand = createAsyncThunk(
  'emails/fetchFolderOnDemand',
  async ({ email, folderId, folderKey, silent = false }, { dispatch, rejectWithValue, getState }) => {
    try {
      if (!silent) dispatch(setLoader(true));
      const res = await axios.post(SERVER_URL + '/api/outlook/fetchMore', {
        email,
        folder: folderId,
        skip: 0,
      });
      const data = (res.data || []).map(sanitizeEmailBody);

      if (folderKey === 'sentitems') {
        // Fetch SES sent emails independently and merge with Graph sent items.
        // This handles the common case where BD sends via SES but receives via Outlook.
        let sesEmails = [];
        try {
          const sesRes = await axios.get(`${SERVER_URL}/api/ses/sent?fromEmail=${encodeURIComponent(email)}&limit=50`);
          sesEmails = sesRes.data?.emails || [];
        } catch (sesErr) {
          console.error('[fetchFolderOnDemand] SES fetch failed:', sesErr?.response?.data || sesErr.message);
        }
        mergeSentItemsAndDispatch(email, data, sesEmails, dispatch);
      } else if (folderKey === 'deleteditems') {
        // Fetch SES soft-deleted emails from DB and merge with Graph's Deleted Items.
        // Using DB (not Redux) ensures deleted SES emails survive hard refresh.
        let sesDeleted = [];
        try {
          const sesDeletedRes = await axios.get(`${SERVER_URL}/api/ses/deleted?fromEmail=${encodeURIComponent(email)}&limit=50`);
          sesDeleted = sesDeletedRes.data?.emails || [];
        } catch (sesErr) {
          console.error('[fetchFolderOnDemand] SES deleted fetch failed:', sesErr?.message);
        }
        const graphIds = new Set(data.map(e => e.id).filter(Boolean));
        const uniqueSesDeleted = sesDeleted.filter(e => !graphIds.has(e.id));
        dispatch(initFolderInState({ user: email, folder: folderKey, data: [...uniqueSesDeleted, ...data] }));
      } else {
        dispatch(initFolderInState({ user: email, folder: folderKey, data }));
      }
    } catch (err) {
      console.error('[fetchFolderOnDemand] Graph fetch failed:', err?.response?.data || err.message);
      // Graph failed (no Outlook token, or Graph error). Still load SES emails for sentitems.
      if (folderKey === 'sentitems') {
        try {
          const sesRes = await axios.get(`${SERVER_URL}/api/ses/sent?fromEmail=${encodeURIComponent(email)}&limit=50`);
          const sesEmails = sesRes.data?.emails || [];
          if (sesEmails.length > 0) {
            dispatch(initFolderInState({ user: email, folder: 'sentitems', data: sesEmails }));
          }
        } catch (sesErr) {
          console.error('[fetchFolderOnDemand] SES fallback also failed:', sesErr?.response?.data || sesErr.message);
          // Both Graph and SES failed — preserve whatever is already in state, show nothing new.
        }
      }
      return rejectWithValue('Failed to load folder emails');
    } finally {
      if (!silent) dispatch(setLoader(false));
    }
  }
);

// Lightweight inbox-only poll. Called every 60s instead of fetchInitialEmails so
// that only inbox state is replaced — other folders the user is currently viewing
// are left untouched.
export const pollInboxSilent = createAsyncThunk(
  'emails/pollInboxSilent',
  async (email, { dispatch, rejectWithValue, getState }) => {
    try {
      const res = await axios.post(SERVER_URL + '/api/outlook/fetchMore', {
        email,
        folder: 'Inbox',
        skip: 0,
      });
      const newInbox = (res.data || []).map(sanitizeEmailBody);

      // Detect new unread emails and notify other parts of the app
      const prevInbox = getState()?.EmailData?.byUser?.[email]?.folders?.inbox?.data || [];
      const prevIds = new Set(prevInbox.map(m => m.id));
      if (prevInbox.length > 0) {
        const newUnread = newInbox.filter(m => !prevIds.has(m.id) && !m.isRead);
        if (newUnread.length > 0) {
          socket.emit('email:newInbox', { count: newUnread.length, email });
          socket.emit('email:newInboxLocal', { count: newUnread.length });
        }
      }

      // Merge new emails into inbox without wiping paginated emails the user has loaded.
      dispatch(mergeInboxPoll({ user: email, newEmails: newInbox }));
    } catch (err) {
      console.error('[pollInboxSilent] failed:', err?.message);
      return rejectWithValue('Poll failed');
    }
  }
);

export const replyToMailThunk = (payload) => async () => {
  const {
    messageId,
    replyContent,
    attachments,
    subject,
    from,
    to,
    cc,
    bcc,
  } = payload;

  try {
    const res = await axios.post(SERVER_URL + '/reply-to-mail', {
      messageId,
      replyContent,
      attachments,
      subject,
      from,
      to,
      cc,
      bcc,
    });
    return res.data;
  } catch (err) {
    throw err?.response?.data || 'Reply failed.';
  }
};

export const forwardEmailThunk = createAsyncThunk(
  'emails/forward',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${SERVER_URL}/forward-email`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err.message);
    }
  }
);

export const sendSharedMailboxEmail = createAsyncThunk(
  'emails/sendSharedMailboxEmail',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${SERVER_URL}/api/sharedemail`, payload);
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || 'Failed to send email.');
    }
  }
);

export const validateOutlook = createAsyncThunk(
  "auth/validateOutlook",
  async ({ email, password }, { rejectWithValue }) => {
    try {

      const encryptedPassword = CryptoJS.AES.encrypt(
        password,
        process.env.REACT_APP_SECRET_KEY || 'bp-dashboard-key'
      ).toString();
      const { data } = await client.mutate({
        mutation: VALIDATE_OUTLOOK,
        variables: { email, password: encryptedPassword },
        fetchPolicy: "no-cache",
      });

      return data.validateOutlook;
    } catch (err) {
      console.error(err);
      return rejectWithValue("Login failed");
    }
  }
);
